import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import BusinessInfoNavigation from '../../components/BusinessInfoNavigation';
import { Field, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import validations from 'validations';
import MapField from '../../components/MapField';
import TextareaArrayField from 'components/TextareaArrayField';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  KEY_ASSETS_MAX_CHARACTERS,
  MIN_REQUIRED_FIELD_ITEMS,
  PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
} from '../../utils';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import ListingSlugField from '../../components/ListingSlugField';
import ListingContactColleagues from '../ListingContactColleagues';
import { Listing, MapValue } from 'models/Listing';
import { Button } from 'ncoded-component-library';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import SelectField from 'components/SelectField';
import useFirstStepValues from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/components/FirstStep/hooks';

import './BusinessInfo.styles.scss';
import './BusinessInfo.styles.responsive.scss';

type BusinessInfoProps = {
  className?: string;
};

const BusinessInfo: React.FC<BusinessInfoProps> = () => {
  const { t } = useTranslation();

  const {
    values: { products, keyAssets, highlights, location },
  } = useFormState<Listing>();
  const { patchListingValue } = useContext(PatchListingContext);
  const { change } = useForm();

  const { dealLocationOptions } = useFirstStepValues();

  const [showPreciseLocation, setShowPreciseLocation] = useState(!!location);

  return (
    <div className="lcw-page">
      <BusinessInfoNavigation />

      <div className="lcw-custom-label-container">
        <h3>{t('keyListingInformation')}</h3>
      </div>

      <div>
        <Field
          name="dealLocation"
          required
          searchable
          className="location-select"
          label={t('location')}
          component={SelectField}
          options={dealLocationOptions}
          validate={validations.required()}
          customOnChange={(dealLocation: string, isValid: boolean) => {
            if (isValid) {
              patchListingValue({
                dealLocation,
              });
            }
          }}
        />

        <Button
          variant="link"
          iconPosition="right"
          icon={ChevrondownIcon}
          className={classNames('lcw-add-more-location', {
            up: showPreciseLocation,
          })}
          onClick={() => {
            if (showPreciseLocation) {
              change('location', null);
            }
            setShowPreciseLocation((prev) => !prev);
          }}
        >
          {t(
            showPreciseLocation
              ? 'hideMoreSpecificLocation'
              : 'addMoreSpecificLocation',
          )}
        </Button>
      </div>

      {showPreciseLocation && (
        <Field
          description={t('locationDescription')}
          name="location"
          component={MapField}
          customOnChange={(location: MapValue, isValid: boolean) => {
            if (isValid) {
              patchListingValue({ location });
            }
          }}
        />
      )}

      <Field
        label={t('listingTitle')}
        name="listingTitle"
        component={InputField}
        validate={validations.required()}
        placeholder={t('enterListingTitle')}
        required
        maxLength={TITLE_MAX_CHARACTERS}
        customOnBlur={(
          e: React.FocusEvent<HTMLInputElement>,
          isValid: boolean,
        ) => {
          const listingTitle = e.target.value;

          if (isValid) {
            patchListingValue({ listingTitle });
          }
        }}
      />
      <ListingSlugField />
      <Field
        label={t('shortDescription')}
        name="shortDescription"
        component={InputField}
        validate={validations.required()}
        placeholder={t('shortDescriptionPlaceholder')}
        maxLength={200}
        required
        customOnBlur={(
          e: React.FocusEvent<HTMLInputElement>,
          isValid: boolean,
        ) => {
          const shortDescription = e.target.value;

          if (isValid) {
            patchListingValue({ shortDescription });
          }
        }}
      />
      <Field
        className="lcw-description-field-textarea"
        label={t('fullDescription')}
        required
        name="businessDescription"
        component={TextAreaField}
        validate={validations.required()}
        placeholder={t('thisWillApearProminently')}
        maxLength={DESCRIPTION_MAX_CHARACTERS}
        customOnBlur={(
          e: React.FocusEvent<HTMLTextAreaElement>,
          isValid: boolean,
        ) => {
          const businessDescription = e.target.value;

          if (isValid) {
            patchListingValue({ businessDescription });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('businessHighlights')}</h3>
        <p>{t('businessHighlightsDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="highlights"
        fieldLabel={t('highlight')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={HIGHLIGHTS_MAX_CHARACTERS}
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
        required
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              highlights: values,
              keyAssets,
              products,
            });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('products/Services')}</h3>
        <p>{t('products/ServicesDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="products"
        fieldLabel={t('products/Services')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={PRODUCTS_AND_SERVICES_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              products: values,
              keyAssets,
              highlights,
            });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('keyAssets')}</h3>
        <p>{t('keyAssetsDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="keyAssets"
        fieldLabel={t('keyAsset')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={KEY_ASSETS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              keyAssets: values,
              products,
              highlights,
            });
          }
        }}
      />

      <ListingContactColleagues />
    </div>
  );
};

export default BusinessInfo;
